import { type FunctionComponent, type ReactNode, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { Edition, useEditionValue } from "shared/lib/edition";

import type { AIEngineResponse } from "scmp-app/components/home-v2/ai-article-list/hooks";
import { useFetchAiArticles } from "scmp-app/components/home-v2/ai-article-list/hooks";
import { AiEngineDataType } from "scmp-app/lib/ai-engine/enums";
import { useGetEntityIdsByQueueItems } from "scmp-app/lib/hooks";
import type {
  clientQueryLoaderHomeV2Query,
  clientQueryLoaderHomeV2Query$data,
} from "scmp-app/queries/__generated__/clientQueryLoaderHomeV2Query.graphql";

export type Props = {
  children?: (payload: Payload) => ReactNode;
  ssrContentEntityIds: string[];
};

type Payload = {
  aiWidgets: Record<
    AiEngineDataType.HomeRecommendedForYou | AiEngineDataType.HomeYouMayHaveMissed,
    AIEngineResponse
  >;
  data: clientQueryLoaderHomeV2Query$data;
  edition: Edition;
  existingEntityIds: string[];
};

/**
 * Client-side data loader for the below-the-fold v2 homepage widgets (section
 * blocks, multimedia, most popular + trending topics) and the two AI engine
 * fetches. Mirrors the v1 HomeClientQueryLoader: the section queues are also
 * read id-only so the AI widgets exclude whatever the section blocks render,
 * in addition to the SSR page content. Everything here suspends, so it must
 * be mounted inside a client-side suspense boundary.
 */
export const HomeV2ClientQueryLoader: FunctionComponent<Props> = ({
  children,
  ssrContentEntityIds,
}) => {
  const edition = useEditionValue();

  const queryReference = useLazyLoadQuery<clientQueryLoaderHomeV2Query>(
    graphql`
      query clientQueryLoaderHomeV2Query(
        $excludeSsrContentEntityIds: [String]!
        $isAsiaEdition: Boolean!
        $isHongKongEdition: Boolean!
        $mostPopularQueueName: String!
        $multimediaQueueName: String!
        $trendingTopicsQueueName: String!
      ) {
        china: queue(filter: { name: "section_top_4" }) {
          items(first: 7, exclude: { entityIds: $excludeSsrContentEntityIds }) {
            edges {
              ...contentUtilitiesGetEntityIdsByQueueItemQueueItemsEdge
            }
          }
        }
        hongKong: queue(filter: { name: "section_top_2" }) @skip(if: $isAsiaEdition) {
          items(first: 7, exclude: { entityIds: $excludeSsrContentEntityIds }) {
            edges {
              ...contentUtilitiesGetEntityIdsByQueueItemQueueItemsEdge
            }
          }
        }
        lifestyle: queue(filter: { name: "section_top_94" }) {
          items(first: 5, exclude: { entityIds: $excludeSsrContentEntityIds }) {
            edges {
              ...contentUtilitiesGetEntityIdsByQueueItemQueueItemsEdge
            }
          }
        }
        peopleCulture: queue(filter: { name: "section_top_318202" }) @skip(if: $isHongKongEdition) {
          items(first: 5, exclude: { entityIds: $excludeSsrContentEntityIds }) {
            edges {
              ...contentUtilitiesGetEntityIdsByQueueItemQueueItemsEdge
            }
          }
        }
        style: queue(filter: { name: "section_top_72" }) {
          items(first: 5, exclude: { entityIds: $excludeSsrContentEntityIds }) {
            edges {
              ...contentUtilitiesGetEntityIdsByQueueItemQueueItemsEdge
            }
          }
        }
        twia: queue(filter: { name: "section_top_323045" }) @include(if: $isAsiaEdition) {
          items(first: 7, exclude: { entityIds: $excludeSsrContentEntityIds }) {
            edges {
              ...contentUtilitiesGetEntityIdsByQueueItemQueueItemsEdge
            }
          }
        }
        wellness: queue(filter: { name: "section_top_24" }) @include(if: $isHongKongEdition) {
          items(first: 5, exclude: { entityIds: $excludeSsrContentEntityIds }) {
            edges {
              ...contentUtilitiesGetEntityIdsByQueueItemQueueItemsEdge
            }
          }
        }
        advertisers: queue(filter: { name: "homepage_brand_post_int" }) {
          items(first: 20) {
            ...advertisersHomeV2QueueItemConnection
          }
        }
        ...mostPopularHomeV2Query @arguments(mostPopularQueueName: $mostPopularQueueName)
        ...multimediaHomeV2Query @arguments(multimediaQueueName: $multimediaQueueName)
        ...sectionTopHomeV2Query
          @arguments(
            isAsiaEdition: $isAsiaEdition
            isHongKongEdition: $isHongKongEdition
            excludeSsrContentEntityIds: $excludeSsrContentEntityIds
          )
        ...trendingTopicHomeV2Topic @arguments(trendingTopicsQueueName: $trendingTopicsQueueName)
      }
    `,
    {
      excludeSsrContentEntityIds: ssrContentEntityIds,
      isAsiaEdition: edition === Edition.Asia,
      isHongKongEdition: edition === Edition.HongKong,
      mostPopularQueueName: "scmp_trending_section_scmp_trending_section_homepage_last_24_hours",
      multimediaQueueName: `visual_stories_top_${edition}`,
      trendingTopicsQueueName: `related_topic_homepage_${edition}_`,
    },
    { fetchPolicy: "store-or-network" },
  );

  const sectionEdges = useMemo(
    () => [
      ...(queryReference?.hongKong?.items?.edges ?? []),
      ...(queryReference?.china?.items?.edges ?? []),
      ...(queryReference?.lifestyle?.items?.edges ?? []),
      ...(queryReference?.peopleCulture?.items?.edges ?? []),
      ...(queryReference?.style?.items?.edges ?? []),
      ...(queryReference?.twia?.items?.edges ?? []),
      ...(queryReference?.wellness?.items?.edges ?? []),
    ],
    [
      queryReference?.china?.items?.edges,
      queryReference?.hongKong?.items?.edges,
      queryReference?.lifestyle?.items?.edges,
      queryReference?.peopleCulture?.items?.edges,
      queryReference?.style?.items?.edges,
      queryReference?.twia?.items?.edges,
      queryReference?.wellness?.items?.edges,
    ],
  );
  const existingEntityIds = useGetEntityIdsByQueueItems(sectionEdges, ssrContentEntityIds);

  const recommendedForYouAIEngineResponse = useFetchAiArticles(
    AiEngineDataType.HomeRecommendedForYou,
    existingEntityIds,
  );

  const youMayHaveMissedAIEngineResponse = useFetchAiArticles(
    AiEngineDataType.HomeYouMayHaveMissed,
    [...existingEntityIds, ...recommendedForYouAIEngineResponse.entityIds],
  );

  return children?.({
    aiWidgets: {
      [AiEngineDataType.HomeRecommendedForYou]: recommendedForYouAIEngineResponse,
      [AiEngineDataType.HomeYouMayHaveMissed]: youMayHaveMissedAIEngineResponse,
    },
    data: queryReference,
    edition,
    existingEntityIds,
  });
};

HomeV2ClientQueryLoader.displayName = "HomeV2ClientQueryLoader";
